import { NumberInput } from "@mantine/core";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { RoundedNumberFormat } from "../../../../utils/RoundedNumberFormat";
import type {
  Vollbenutzungsstunden,
  VollbenutzungsstundenItem
} from "../VollbenutzungsstundenList";
import { computeCumulativeVBH } from "../VollbenutzungsstundenList";

export interface VollbenutzungsstundenTableProps {
  vollbenutzungsstunden: Vollbenutzungsstunden;
  onUpdateValues: (
    currentVBH: VollbenutzungsstundenItem,
    newHours: number | null
  ) => void;
}

function VollbenutzungsstundenTable({
  vollbenutzungsstunden,
  onUpdateValues
}: VollbenutzungsstundenTableProps) {
  const columns = useMemo<Array<MRT_ColumnDef<VollbenutzungsstundenItem>>>(
    () => [
      {
        accessorKey: "year",
        header: "Jahr",
        enableSorting: false,
        Cell: ({ cell }) => <p>{cell.getValue<number>()}</p>
      },
      {
        accessorKey: "hours",
        header: "zuschlagsfähige Vollbenutzungsstunden",
        enableSorting: false,
        Cell: ({ cell, row }) => (
          <VBSCell
            original={row.original}
            updateValues={onUpdateValues}
            value={cell.getValue<number | null>()}
          />
        )
      },
      {
        accessorFn: (row) => row.year,
        id: "cumulative",
        header: "kumulierte Vollbenutzungsstunden zum 1.1. des Folgejahres",
        enableSorting: false,
        Cell: ({ row }) => (
          <VBSCumulatedCell
            allValues={vollbenutzungsstunden}
            currentYear={row.original.year}
          />
        )
      }
    ],
    [vollbenutzungsstunden, onUpdateValues]
  );

  const defaultOptions = getDefaultMRTOptions<VollbenutzungsstundenItem>({
    emptyRowsFallbackText: "Keine Daten verfügbar",
    initialState: {
      sorting: [
        {
          id: "year",
          desc: false
        }
      ]
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    enableFilters: false,
    enableTopToolbar: false
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: vollbenutzungsstunden
  });

  return <MantineReactTable table={table} />;
}

interface VBSCumulatedCellProps {
  allValues: Vollbenutzungsstunden;
  currentYear: number;
}

function VBSCumulatedCell({ allValues, currentYear }: VBSCumulatedCellProps) {
  const hours = computeCumulativeVBH(allValues).find(
    (value) => value.year === currentYear
  )?.hours;
  return <RoundedNumberFormat displayType="text" value={hours ?? ""} />;
}

interface VBSCellProps {
  value: null | number;
  original: VollbenutzungsstundenItem;
  updateValues: (
    currentVBH: VollbenutzungsstundenItem,
    newHours: number | null
  ) => void;
}

function VBSCell({
  value: initialValue,
  original,
  updateValues
}: VBSCellProps) {
  const [value, setValue] = useState(initialValue);
  return (
    <NumberInput
      allowDecimal={false}
      value={value ? value : ""}
      onBlur={() => updateValues(original, value)}
      onChange={(targetValue) => {
        if (typeof targetValue === "number") {
          setValue(targetValue);
        } else {
          const parsedValue = parseFloat(targetValue);
          setValue(isNaN(parsedValue) ? null : parsedValue);
        }
      }}
    />
  );
}

export { VollbenutzungsstundenTable };
